import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { authService } from '../services/authService';
import { useFlag, useFlagsReady, FLAGS } from '../FlagProvider';
import { LOGIN_URL } from '../config';
import { can, FEATURES } from '../lib/permissions';

// Icon components
const NetworkIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const TargetIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    <circle cx="12" cy="12" r="3" strokeWidth={2} fill="none" />
  </svg>
);

const MagnifyingGlassIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const ProductivityGaugeIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const SettingsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

// Broadcast arcs: a source is a channel we listen to, not a document we store.
const SignalIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <circle cx="5" cy="19" r="1.5" strokeWidth={2} />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 11a9 9 0 019 9M5 5a15 15 0 0115 15" />
  </svg>
);

const HelpIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const getIcon = (iconName) => {
  switch (iconName) {
    case 'Network':
      return <NetworkIcon />;
    case 'Target':
      return <TargetIcon />;
    case 'MagnifyingGlass':
      return <MagnifyingGlassIcon />;
    case 'ProductivityGauge':
      return <ProductivityGaugeIcon />;
    case 'Settings':
      return <SettingsIcon />;
    case 'Signal':
      return <SignalIcon />;
    default:
      return null;
  }
};

export default function Layout({ children, user }) {
  const location = useLocation();
  const [showProfilePopup, setShowProfilePopup] = useState(false);
  // Write-only on purpose: the chosen file is held for the upload the TODO in
  // handleAvatarChange describes. Nothing READS it -- avatarPreview is what the
  // avatar renders from -- so the value binding is dropped rather than kept
  // unused. Deleting the setter too would throw away the file the uploader needs.
  const [, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const popupRef = useRef(null);
  const fileInputRef = useRef(null);

  // Close popup when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        // Check if click is not on the user profile section
        const userSection = event.target.closest('[data-user-profile]');
        if (!userSection) {
          setShowProfilePopup(false);
        }
      }
    }

    if (showProfilePopup) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showProfilePopup]);

  const handleLogout = async () => {
    await authService.logout();
    window.location.href = `${LOGIN_URL}/login`;
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
      // TODO: Upload avatar to API
    }
  };

  // Which surfaces exist is runtime configuration, not a build-time constant.
  // For the three enforced flags below, a flag that is off hides the nav entry
  // AND 404s the API route behind it (cmd/api requireFlag), so this is
  // presentation of a decision made server-side, not the decision itself.
  // Deep Research's flag decides only its "Soon" badge -- see below.
  //
  // When the configuration cannot be fetched every flag reads false, which is the
  // same thing !process.env.REACT_APP_CORE_API_URL used to express: no Go core
  // API reachable, so do not offer pages that need one.
  //
  // useFlag reads context, so it is a hook -- all four keys are read
  // unconditionally on every render. Readiness is combined afterward, as plain
  // boolean logic, never by skipping the hook call itself (that would call a
  // different number of hooks depending on flagsReady, which React does not
  // allow).
  const flagsReady = useFlagsReady();
  const candidateGraphFlag = useFlag(FLAGS.candidateGraph);
  const targetedOutreachFlag = useFlag(FLAGS.targetedOutreach);
  const signalSourcesFlag = useFlag(FLAGS.signalSources);
  const deepResearchFlag = useFlag(FLAGS.deepResearch);

  // An enforced flag decides whether the entry EXISTS; deep_research's decides
  // only how it looks. Readiness is required for the first kind and irrelevant to
  // the second: an entry that appears and then vanishes is worse than one that
  // appears a moment late, while a badge that settles a moment late costs nothing.
  const candidateGraphOn = flagsReady && candidateGraphFlag;
  const targetedOutreachOn = flagsReady && targetedOutreachFlag;
  const signalSourcesOn = flagsReady && signalSourcesFlag;
  const deepResearchComingSoon = !deepResearchFlag;

  // One entry table per nav group. WHO sees an entry comes from the shared
  // permission matrix (web/src/lib/permissions.json, generated from
  // internal/authz); WHAT an entry looks like — its icon, href and group — stays
  // here, because that is presentation.
  //
  // Two gates remove an entry and they compose the same way:
  //
  //   role denied   -> filtered out below, not in the DOM.
  //   flag off      -> filtered out below, not in the DOM.
  //
  // They are still different questions -- "is this surface part of your job" and
  // "does this deployment serve it at all" -- and the API enforces both
  // independently (403 and 404 respectively). The nav simply has one way to say no.
  //
  // `comingSoon` is what is left of presentation: it marks a surface that IS
  // served and IS yours but is not built. Deep Research is its only user.
  //
  // Design: docs/superpowers/specs/2026-08-12-feature-flag-enforcement-design.md
  const TOP_NAV = [
    { name: 'Candidate Graph', href: '/candidates', icon: 'Network', feature: FEATURES.candidateGraph, enabled: candidateGraphOn, comingSoon: false },
    { name: 'Targeted Outreach', href: '/targeted-outreach', icon: 'Target', feature: FEATURES.targetedOutreach, enabled: targetedOutreachOn, comingSoon: false },
    { name: 'Deep Research', href: '/deep-research', icon: 'MagnifyingGlass', feature: FEATURES.deepResearch, enabled: true, comingSoon: deepResearchComingSoon },
    // No "Soon" badge: this page is fully built (overview / settings / users /
    // integrations) and is where an admin now lands after signing in.
    { name: 'Admin Console', href: '/admin', icon: 'Settings', feature: FEATURES.adminConsole, enabled: true, comingSoon: false },
  ];

  // Quality and the pipeline are two views of one thing, so they share one entry.
  //
  // Signal Sources sits beside Data because it is the same kind of thing: a
  // tenant-facing view over their own records, not an admin setting.
  const BOTTOM_NAV = [
    { name: 'Manage Team', href: '/manage-team', icon: 'Settings', feature: FEATURES.manageTeam, enabled: true, comingSoon: false },
    { name: 'Data', href: '/admin/data', icon: 'ProductivityGauge', feature: FEATURES.dataQuality, enabled: true, comingSoon: false },
    { name: 'Signal Sources', href: '/admin/sources', icon: 'Signal', feature: FEATURES.signalSources, enabled: signalSourcesOn, comingSoon: false },
  ];

  const role = user?.role || '';
  const topNavigation = TOP_NAV.filter((item) => item.enabled && can(role, item.feature));
  const bottomNavigation = BOTTOM_NAV.filter((item) => item.enabled && can(role, item.feature));
  const titleNavigation = [...topNavigation, ...bottomNavigation];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar - 256px width as per design doc */}
      <div className="fixed inset-y-0 left-0 w-64 bg-gray-900 text-white flex flex-col">
        {/* Logo */}
        <div className="flex items-center justify-center h-16 px-4 border-b border-gray-800">
          <img src="/sc_dark.png" alt="SignalCue" className="h-6 w-auto" />
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 flex flex-col justify-between">
          <div className="space-y-2">
            {topNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-[#1B3A6B] text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <div className="flex items-center">
                    <span className="mr-3">{getIcon(item.icon)}</span>
                    <span className="font-medium">{item.name}</span>
                  </div>
                  {item.comingSoon && (
                    <span className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded">Soon</span>
                  )}
                </Link>
              );
            })}
          </div>

          <div className="space-y-2">
            {bottomNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-[#1B3A6B] text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <div className="flex items-center">
                    <span className="mr-3">{getIcon(item.icon)}</span>
                    <span className="font-medium">{item.name}</span>
                  </div>
                  {item.comingSoon && (
                    <span className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded">Soon</span>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* User Info */}
        <div className="px-4 py-4 border-t border-gray-800 space-y-2">
          <Link
            to="/help"
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-300 rounded-lg hover:bg-gray-800 hover:text-white transition-colors"
          >
            <span className="mr-3"><HelpIcon /></span>
            <span>Help & Support</span>
          </Link>
          <button
            onClick={handleLogout}
            className="w-full px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="pl-64">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              {/* Left side - Page Title */}
              <div className="flex items-center">
                <h1 className="text-2xl font-bold text-gray-900">
                  {titleNavigation.find(item => 
                    location.pathname === item.href || 
                    (item.href === '/candidates' && location.pathname === '/')
                  )?.name || 'SignalCue'}
                </h1>
              </div>
              {/* Right side - Notifications and User Profile */}
              <div className="flex items-center space-x-4">
                {/* Bell icon for notifications */}
                <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </button>
                {/* Vertical divider */}
                <div className="h-8 w-px bg-gray-300"></div>
                {/* User profile section */}
                <div className="relative" data-user-profile>
                  <button
                    onClick={() => setShowProfilePopup(!showProfilePopup)}
                    className="flex items-center space-x-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors"
                  >
                    <div className="text-right">
                      <div className="text-sm font-semibold text-gray-900">
                        {user?.display_name || 'User'}
                      </div>
                      <div className="text-xs text-gray-500 capitalize">
                        {user?.role === 'recruiter' ? 'Senior Recruiter' : 
                         user?.role === 'manager' ? 'Manager' : 
                         user?.role === 'admin' ? 'Administrator' : 'User'}
                      </div>
                    </div>
                    {/* Avatar with initials - teal accent background */}
                    <div className="w-10 h-10 rounded-full bg-[#00C6AE] flex items-center justify-center text-white font-semibold text-sm overflow-hidden">
                      {avatarPreview ? (
                        <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                      ) : user?.display_name ? (
                        user.display_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                      ) : (
                        'U'
                      )}
                    </div>
                  </button>

                  {/* Profile Popup */}
                  {showProfilePopup && (
                    <div
                      ref={popupRef}
                      className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50"
                    >
                      <div className="p-6">
                        {/* Header */}
                        <div className="flex items-center space-x-4 mb-6">
                          <div className="relative">
                            <div className="w-16 h-16 rounded-full bg-[#00C6AE] flex items-center justify-center text-white font-semibold text-xl overflow-hidden">
                              {avatarPreview ? (
                                <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                              ) : user?.display_name ? (
                                user.display_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                              ) : (
                                'U'
                              )}
                            </div>
                            <button
                              onClick={handleAvatarClick}
                              className="absolute bottom-0 right-0 w-6 h-6 bg-[#1B3A6B] rounded-full flex items-center justify-center text-white text-xs hover:bg-[#1B3A6B]/90 transition-colors"
                              title="Upload photo"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </button>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/*"
                              onChange={handleAvatarChange}
                              className="hidden"
                            />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {user?.display_name || 'User'}
                            </h3>
                            <p className="text-sm text-gray-500 capitalize">
                              {user?.role === 'recruiter' ? 'Senior Recruiter' : 
                               user?.role === 'manager' ? 'Manager' : 
                               user?.role === 'admin' ? 'Administrator' : 'User'}
                            </p>
                          </div>
                        </div>

                        {/* User Information */}
                        <div className="space-y-4 mb-6">
                          <div>
                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</label>
                            <p className="text-sm text-gray-900 mt-1">{user?.email || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Role</label>
                            <p className="text-sm text-gray-900 mt-1 capitalize">
                              {user?.role === 'recruiter' ? 'Senior Recruiter' : 
                               user?.role === 'manager' ? 'Manager' : 
                               user?.role === 'admin' ? 'Administrator' : user?.role || 'N/A'}
                            </p>
                          </div>
                          {user?.user_id && (
                            <div>
                              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">User ID</label>
                              <p className="text-sm text-gray-900 mt-1 font-mono">{user.user_id}</p>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="border-t border-gray-200 pt-4 space-y-2">
                          <button
                            onClick={() => {
                              setShowProfilePopup(false);
                              // TODO: Navigate to profile settings
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                          >
                            Profile Settings
                          </button>
                          <button
                            onClick={() => {
                              setShowProfilePopup(false);
                              // TODO: Navigate to account settings
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                          >
                            Account Settings
                          </button>
                          <button
                            onClick={handleLogout}
                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            Log Out
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
